import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/SkillEditorBody.css';

export interface SkillEditorBodyProps {
  mode: 'edit' | 'preview';
  onModeChange: (m: 'edit' | 'preview') => void;
  body: string;
  onBodyChange: (v: string) => void;
  loading?: boolean;
  textEditorRef: React.RefObject<HTMLTextAreaElement | null>;
  insertMd: (before: string, after: string, linePrefix?: string) => void;
  hint?: string;
  readOnly?: boolean;
  onActivate?: () => void;
  hideModeTabs?: boolean;
  placeholder?: string;
  /** When set, the Edit toolbar shows an "Import doc" button; the chosen file is handed to this
   * callback (the page converts it to Markdown and inserts the result into the body). */
  onImportDocument?: (file: File) => void;
  /** accept= list for the import file picker. */
  importAccept?: string;
  /** Disables the import button while a conversion is in flight. */
  importing?: boolean;
}

const ImportDocButton: React.FC<{
  accept?: string; importing: boolean; disabled: boolean; onPick: (file: File) => void;
}> = ({ accept, importing, disabled, onPick }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <span className="ca-fp-md-toolbar__div" />
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }} />
      <button type="button"
        title="Import a document (PDF, Word, PowerPoint, Excel, ...) - its contents are added to the prompt as Markdown"
        disabled={disabled || importing}
        onClick={() => { if (!disabled && !importing) inputRef.current?.click(); }}>
        {importing ? 'Importing…' : 'Import doc'}
      </button>
    </>
  );
};

const SkillEditorBody: React.FC<SkillEditorBodyProps> = ({
  mode, onModeChange, body, onBodyChange, loading = false, textEditorRef, insertMd, hint, readOnly, onActivate,
  hideModeTabs = false, placeholder, onImportDocument, importAccept, importing = false,
}) => (
  <div className={`ca-fp-editor__body${mode === 'preview' ? ' ca-fp-editor__body--preview' : ''}`}>
    {/* Toolbar row */}
    <div className="ca-fp-editor__toolbar-row">
      {!hideModeTabs && (
        <div className="ca-fp-editor__mode-tabs">
          <button className={`ca-fp-editor__mode-tab${mode === 'edit' ? ' ca-fp-editor__mode-tab--on' : ''}`}
            onClick={() => !readOnly && onModeChange('edit')}>Edit</button>
          <button className={`ca-fp-editor__mode-tab${mode === 'preview' ? ' ca-fp-editor__mode-tab--on' : ''}`}
            onClick={() => onModeChange('preview')}>Preview</button>
        </div>
      )}
      {mode === 'edit' && (
        <div className={`ca-fp-md-toolbar${readOnly ? ' ca-fp-md-toolbar--disabled' : ''}`}>
          <button title="Bold"          onClick={() => !readOnly && insertMd('**', '**')}><b>B</b></button>
          <button title="Italic"        onClick={() => !readOnly && insertMd('*', '*')}><em>I</em></button>
          <span className="ca-fp-md-toolbar__div" />
          <button title="Heading 1"     onClick={() => !readOnly && insertMd('', '', '# ')}>H1</button>
          <button title="Heading 2"     onClick={() => !readOnly && insertMd('', '', '## ')}>H2</button>
          <button title="Heading 3"     onClick={() => !readOnly && insertMd('', '', '### ')}>H3</button>
          <span className="ca-fp-md-toolbar__div" />
          <button title="Bullet list"   onClick={() => !readOnly && insertMd('', '', '- ')}>UL</button>
          <button title="Numbered list" onClick={() => !readOnly && insertMd('', '', '1. ')}>OL</button>
          <span className="ca-fp-md-toolbar__div" />
          <button title="Inline code"   onClick={() => !readOnly && insertMd('`', '`')}>{"`  `"}</button>
          <button title="Blockquote"    onClick={() => !readOnly && insertMd('', '', '> ')}>❝</button>
          <button title="HR"            onClick={() => { if (!readOnly) { onBodyChange(body + '\n---\n'); setTimeout(() => textEditorRef.current?.focus(), 0); } }}>—</button>
          {onImportDocument && (
            <ImportDocButton accept={importAccept} importing={importing} disabled={!!readOnly} onPick={onImportDocument} />
          )}
        </div>
      )}
      <span className="ca-fp-editor__charcount">{body.length.toLocaleString()} chars</span>
    </div>

    {loading ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <div className="ca-fp-skel-bone" style={{ width: '42%', height: 12, borderRadius: 6 }} />
        <div className="ca-fp-skel-bone" style={{ width: '68%', height: 12, borderRadius: 6 }} />
        <div className="ca-fp-skel-bone" style={{ width: '55%', height: 12, borderRadius: 6 }} />
        <div className="ca-fp-skel-bone" style={{ width: '100%', flex: 1, minHeight: 280, borderRadius: 10 }} />
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.8 }}>Loading skill content...</span>
      </div>
    ) : mode === 'edit' ? (
      <textarea
        ref={textEditorRef}
        className={`ca-fp-editor__textarea${readOnly ? ' ca-fp-editor__textarea--readonly' : ''}`}
        placeholder={placeholder ?? `# Skill Title\n\nDescribe what the agent should do.\n\n## Instructions\n- Step 1\n- Step 2`}
        value={body}
        onChange={e => !readOnly && onBodyChange(e.target.value)}
        onClick={() => readOnly && onActivate?.()}
        readOnly={readOnly}
        spellCheck={false}
      />
    ) : (
      <div className="ca-fp-editor__preview">
        {body.trim()
          ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
          : <span className="ca-fp-editor__preview-empty">Nothing to preview yet.</span>
        }
      </div>
    )}

    {hint && <p className="ca-fp-editor__hint">{hint}</p>}
  </div>
);

export default SkillEditorBody;
